export class UrlSafetyHelper {
  private static readonly DISALLOWED_PROTOCOLS = new Set([
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
  ]);

  static sanitize(value: string | null | undefined): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().replace(/\s+/g, ' ');
  }

  static normalize(value: string | null | undefined): string {
    const input = this.sanitize(value);
    if (!input) {
      return '';
    }

    if (/^https?:\/\//i.test(input)) {
      return input;
    }

    if (input.startsWith('//')) {
      return `https:${input}`;
    }

    return `https://${input}`;
  }

  static isSuspiciousHostname(hostname: string): boolean {
    const normalized = hostname.toLowerCase();

    if (!normalized || normalized === 'localhost') {
      return true;
    }

    if (normalized.startsWith('localhost.')) {
      return true;
    }

    if (/^\d+\.\d+\.\d+\.\d+$/.test(normalized)) {
      return true;
    }

    if (normalized === '127.0.0.1' || normalized === '[::1]') {
      return true;
    }

    return (
      normalized.startsWith('127.') ||
      normalized.startsWith('10.') ||
      normalized.startsWith('192.168.')
    );
  }

  static getSafeUrl(value: string | null | undefined): string | null {
    const input = this.sanitize(value);
    if (!input) {
      return null;
    }

    let normalized = this.normalize(input);
    if (!normalized) {
      return null;
    }

    try {
      const url = new URL(normalized);
      const protocol = url.protocol.toLowerCase();
      const hostname = url.hostname.toLowerCase();

      if (this.DISALLOWED_PROTOCOLS.has(protocol)) {
        return null;
      }

      if (!['http:', 'https:'].includes(protocol)) {
        return null;
      }

      if (!hostname || hostname.length < 3) {
        return null;
      }

      if (this.isSuspiciousHostname(hostname)) {
        return null;
      }

      return url.toString();
    } catch {
      return null;
    }
  }

  static isSafeExternalUrl(value: string | null | undefined): boolean {
    return !!this.getSafeUrl(value);
  }

  static sanitizeForStorage(value: string | null | undefined): string | null {
    return this.getSafeUrl(value);
  }
}
